import { Component, type ReactNode } from 'react'
import { Button } from '../ui/Button'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="min-h-screen bg-gradient-to-b from-midnight via-midnight-light to-midnight flex items-center justify-center p-6">
          <div className="glass rounded-2xl p-8 max-w-md text-center space-y-6">
            <span className="text-5xl block">😅</span>
            <h1 className="text-2xl font-bold text-cream">Oops! Something went wrong</h1>
            <p className="text-cream/70">
              Don't worry, your progress is saved. Let's get you back on track.
            </p>
            {this.state.error && (
              <details className="text-left">
                <summary className="text-cream/50 text-sm cursor-pointer hover:text-cream/70">
                  Technical details
                </summary>
                <pre className="mt-2 p-3 bg-black/30 rounded-lg text-xs text-red-400 overflow-auto">
                  {this.state.error.message}
                </pre>
              </details>
            )}
            <div className="flex gap-3 pt-2">
              <Button variant="ghost" onClick={this.handleReset} className="flex-1">
                Try Again
              </Button>
              <Button variant="primary" onClick={this.handleGoHome} className="flex-1">
                Go Home
              </Button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
